<!-- filepath: c:\Users\MichaelKreisler\Documents\Projekte\Fraport\Forecasting\DT_Fraport_AI_Core\src\components\TaskView.vue -->
<template>
    <div>
      <!-- Task Tabs -->
      <div class="border-b border-gray-200">
        <div class="flex">
          <button
            @click="activeTab = 'my-tasks'"
            class="px-4 py-2 border-b-2 font-medium transition-colors duration-200"
            :class="activeTab === 'my-tasks' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'"
          >
            My Tasks
          </button>
          <button
            @click="activeTab = 'all-tasks'"
            class="px-4 py-2 border-b-2 font-medium transition-colors duration-200"
            :class="activeTab === 'all-tasks' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'"
          >
            All Tasks
          </button>
          <button
            v-if="canViewReminders"
            @click="activeTab = 'reminders'"
            class="px-4 py-2 border-b-2 font-medium transition-colors duration-200 flex items-center space-x-2"
            :class="activeTab === 'reminders' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'"
          >
            <span>Reminders</span>
            <span
              v-if="overdueRemindersCount > 0"
              class="bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
            >
              {{ overdueRemindersCount }}
            </span>
          </button>
        </div>
      </div>
  
      <!-- Task Content for both My Tasks and All Tasks -->
      <div v-if="activeTab === 'my-tasks' || activeTab === 'all-tasks'" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
              <th scope="col" class="relative px-6 py-3">
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="task in activeTab === 'my-tasks' ? myTasks : allTasks" :key="task.id">
              <tr>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ task.document_name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ task.user_name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {{ task.action }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm" :class="getDueDateClass(task.due_at)">
                    {{ formatDate(task.due_at) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    @click="toggleTaskExpanded(task.id)"
                    class="text-primary hover:text-primary-dark transition-transform duration-200"
                    :class="{ 'transform rotate-180': expandedTaskId === task.id }"
                  >
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                </td>
              </tr>
              
              <!-- Expanded Actions Section -->
              <tr v-if="expandedTaskId === task.id">
                <td colspan="5" class="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div class="flex space-x-3">
                    <!-- Show Send Reminder button only if task action is DOWNLOAD -->
                    <button
                      v-if="task.action === 'DOWNLOAD' && canViewReminders"
                      @click="showTaskMessage(task.document_id, task.user_name, task.user_email, task.action)"
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                      Send Reminder
                    </button>
                    
                    <!-- Show message when no actions are available -->
                    <div v-else class="text-sm text-gray-500 italic">
                      No actions available
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            
            <!-- Empty state when no tasks are available -->
            <tr v-if="(activeTab === 'my-tasks' ? myTasks : allTasks).length === 0">
              <td colspan="5" class="px-6 py-10 text-center">
                <p class="text-gray-500">No tasks available</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
  
      <!-- Reminders Tab -->
      <div v-if="activeTab === 'reminders' && canViewReminders" class="overflow-x-auto">
        <RemindersTab :reminders="reminders" />
      </div>
      
      <!-- Email Review Modal -->
      <EmailReviewModal
        :is-open="showEmailModal"
        :process-step-type="currentTaskData.action"
        :recipient-email="currentTaskData.email"
        :template-variables="currentTaskData"
        @close="closeEmailModal"
        @send-success="handleEmailSendSuccess"
      />
    </div>
  </template>
  
  <script setup lang="ts">
  import { ref } from 'vue';
  import RemindersTab from './RemindersTab.vue';
  import EmailReviewModal from '../EmailReviewModal.vue';
  import { TaskDocProfile } from '@/features/task-management';
  import { useEmailReminder } from '@/composables/useEmailReminder';
  
  // Props with proper typing
  defineProps({
    myTasks: {
      type: Array as () => TaskDocProfile[],
      required: true
    },
    allTasks: {
      type: Array as () => TaskDocProfile[],
      required: true
    },
    reminders: {
      type: Array as () => TaskDocProfile[],
      required: true
    },
    overdueRemindersCount: {
      type: Number,
      required: true
    },
    canViewReminders: {
      type: Boolean,
      required: true
    }
  });
  
  // State
  const activeTab = ref('my-tasks');
  const expandedTaskId = ref<string | null>(null);
  
  // Use the email reminder composable
  const {
    showEmailModal,
    currentTaskData,
    showEmailReminderModal,
    closeEmailModal,
    handleEmailSendSuccess
  } = useEmailReminder();
  
  // Alias the showEmailReminderModal function to maintain the original function name
  const showTaskMessage = showEmailReminderModal;
  
  // Toggle expanded task
  const toggleTaskExpanded = (taskId: string) => {
    if (expandedTaskId.value === taskId) {
      expandedTaskId.value = null;
    } else {
      expandedTaskId.value = taskId;
    }
  };
  
  
  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Get due date class based on date
  const getDueDateClass = (dateString: string | null | undefined) => {
    if (!dateString) return 'text-gray-500';
    
    const dueDate = new Date(dateString);
    const today = new Date();
    
    // Reset time part for accurate day calculation
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    if (dueDate < today) {
      return 'text-red-500'; // Overdue
    } else if (dueDate.getTime() === today.getTime()) {
      return 'text-yellow-500'; // Due today
    } else {
      return 'text-gray-500'; // Future
    }
  };
  </script>